<template>
  <VDialog v-model:visible="visible" :placement="file ? 'fullscreen' : 'right'" :title="$t('import-users')" width="40%">
    <VLoadingDots v-if="loading" mask />

    <div v-if="!file">
      <I18nT class="mb-5" keypath="you-can-import-users-using-a-file" scope="global" tag="p">
        <template #csv>
          <a href="samples/users-import.csv" rel="noopener" target="_blank">{{ $t('csv') }}</a>
        </template>
        <template #json>
          <a href="samples/users-import.json" rel="noopener" target="_blank">{{ $t('json') }}</a>
        </template>
      </I18nT>

      <VUpload accept=".csv, .json" :disabled="$inSafeGhostMode" drag @update:files="getFile" />
    </div>

    <UploadUsersPreview v-else v-model:file="file" v-model:rows="rows" v-model:valid="valid" />

    <template v-if="file" #footer>
      <VButton :label="$t('cancel')" type="secondary" @click="reset" />
      <VButton :disabled="$inSafeGhostMode || importUsersDisabled.disabled" :disabled-help="importUsersDisabledHelp" :label="$t('import')" :loading @click="confirm" />
    </template>
  </VDialog>
</template>

<script setup lang="ts">
import type { UserRow } from './UploadUsersPreview.vue'

const visible = defineModel<boolean>('visible')

const loading = ref(false)
const file = ref<File>()
const valid = ref(false)
const rows = ref<UserRow[]>([])
const importUsersDisabled = computed(() => stores.organization.canInviteUsers(Math.max(0, rows.value.length - 1)))
const importUsersDisabledHelp = computed(() => ({
  [$t('there-are-invalid-records-to-import')]: !valid.value && rows.value.length > 1,
  [$t('there-are-no-valid-records-to-import')]: rows.value.length <= 1,
  [String(importUsersDisabled.value.help)]: importUsersDisabled.value.disabled,
}))

watch(visible, (newVal) => {
  if (!newVal) reset()
})

function reset() {
  file.value = undefined
  loading.value = false
  valid.value = false
}

async function confirm() {
  const users = rows.value.slice(0, -1)
  if (!users) return

  await $confirm({
    title: $t('import-users'),
    message: $t(
      'if-you-proceed-with-this-action-count-user-will-be-imported-into-your-organisation-or-if-you-proceed-with-this-action-count-users-will-be-imported-into-your-organisation-please-note-that-it-can-take-some-time-before-all-count-users-are-visible-in-the-organisation',
      users.length,
    ),
  })

  loading.value = true

  try {
    const { data } = await api.post('users/import', { users })
    stores.users._updateUsers(data)
    $message.success($t('count-users-are-being-created-and-an-email-will-be-sent-to-their-addresses', users.length))
    file.value = undefined
  } catch {
    $message.error($t('api-error'))
  } finally {
    reset()
  }
}

function getFile(files: File[]) {
  file.value = files[0]
}
</script>
